import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import UserAvatar from "@/components/UserAvatar";
import ResidentDetail from "@/components/ResidentDetail";
import UploadButton from "@/components/UploadButton";
import { fileDownloadUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Search, Trash2, BedDouble, Phone, Stethoscope, AlertTriangle, Utensils, FileText, CheckCircle2, Circle, Clock, MoreHorizontal, Pencil } from "lucide-react";
import { toast } from "sonner";

const CAT_KEYS = ["medication", "hygiene", "meal", "cleaning", "wellness", "appointment", "behaviour", "documentation", "general"];
const EMPTY = { full_name: "", first_name: "", last_name: "", user_code: "", photo: null, date_of_birth: "", room_number: "", admission_date: "", treating_team: "",
  physician_name: "", physician_phone: "", family_contacts: [], emergency_contacts: [], phones: [], emails: [],
  medical_conditions: [], allergies: [], dietary_restrictions: [], procedures: "", care_instructions: "", notes: "", documents: [], active: true };

const csv = (arr) => (arr || []).join(", ");
const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

function Field({ label, children, className = "" }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;
}

function TaskDialog({ open, onOpenChange, resident, employees, task, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({});
  useEffect(() => { if (open) setForm(task || { resident_id: resident?.id, title: "", category: "medication", description: "", date: new Date().toISOString().slice(0, 10), time: "08:00", priority: "medium", assigned_to: "", require_photo: false }); }, [open, task, resident]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...form, resident_id: resident.id };
      if (!payload.assigned_to) payload.assigned_to = null;
      if (task) await api.put(`/residents/tasks/${task.id}`, payload); else await api.post("/residents/tasks", payload);
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("assignTask")} — {resident?.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label={t("title")}><Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="rtask-title" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("category")}>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="rtask-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CAT_KEYS.map((c) => <SelectItem key={c} value={c}>{t(c)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("priority")}>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="high">{t("high")}</SelectItem><SelectItem value="medium">{t("medium")}</SelectItem><SelectItem value="low">{t("low")}</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label={t("dueDate")}><Input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} /></Field>
            <Field label={t("time")}><Input type="time" value={form.time || ""} onChange={(e) => set("time", e.target.value)} data-testid="rtask-time" /></Field>
          </div>
          <Field label={t("assignTo")}>
            <Select value={form.assigned_to || ""} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger><SelectValue placeholder={t("unassigned")} /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="rp" checked={!!form.require_photo} onChange={(e) => set("require_photo", e.target.checked)} className="w-4 h-4" />
            <Label htmlFor="rp">{t("requirePhoto")}</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={save} data-testid="save-rtask">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResidentDialog({ open, onOpenChange, resident, employees, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [tasks, setTasks] = useState([]);
  const [taskDlg, setTaskDlg] = useState(false);
  const isEdit = !!resident;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm(resident ? { ...EMPTY, ...resident } : EMPTY);
      if (resident) api.get(`/residents/${resident.id}/tasks`).then((r) => setTasks(r.data));
      else setTasks([]);
    }
  }, [open, resident]);

  const save = async () => {
    try {
      if (isEdit) await api.put(`/residents/${resident.id}`, form); else await api.post("/residents", form);
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reloadTasks = () => resident && api.get(`/residents/${resident.id}/tasks`).then((r) => setTasks(r.data));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle>{isEdit ? t("editResident") : t("addResident")}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-4">
          <UserAvatar name={`${form.first_name || ""} ${form.last_name || ""}`.trim() || form.full_name} src={form.photo} size={64} />
          <UploadButton label={t("profilePicture")} accept="image/*" category="resident" testId="upload-resident-photo"
            onUploaded={(d) => set("photo", `file:${d.id}`)} />
        </div>
        <Tabs defaultValue="profile">
          <TabsList className="grid grid-cols-4">
            <TabsTrigger value="profile" data-testid="rtab-profile">{t("profile")}</TabsTrigger>
            <TabsTrigger value="medical" data-testid="rtab-medical">Medical</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="rtab-contacts">{t("familyContacts")}</TabsTrigger>
            <TabsTrigger value="tasks" disabled={!isEdit} data-testid="rtab-tasks">{t("careHistory")}</TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("firstName")}><Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="resident-first-name" /></Field>
              <Field label={t("lastName")}><Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="resident-last-name" /></Field>
              <Field label={t("userCode")}><Input value={form.user_code || ""} onChange={(e) => set("user_code", e.target.value)} data-testid="resident-user-code" /></Field>
              <Field label={t("roomNumber")}><Input value={form.room_number || ""} onChange={(e) => set("room_number", e.target.value)} data-testid="resident-room" /></Field>
              <Field label={t("dateOfBirth")}><Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
              <Field label={t("admissionDate")}><Input type="date" value={form.admission_date || ""} onChange={(e) => set("admission_date", e.target.value)} /></Field>
              <Field label={t("treatingTeam")}><Input value={form.treating_team || ""} onChange={(e) => set("treating_team", e.target.value)} /></Field>
              <Field label={t("physician")}><Input value={form.physician_name || ""} onChange={(e) => set("physician_name", e.target.value)} /></Field>
              <Field label={t("physicianPhone")}><Input value={form.physician_phone || ""} onChange={(e) => set("physician_phone", e.target.value)} /></Field>
              <Field label={`${t("phone")} (${t("separateComma")})`}><Input value={csv(form.phones)} onChange={(e) => set("phones", toArr(e.target.value))} /></Field>
            </div>
            <Field label={t("notes")}><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
          </TabsContent>
          <TabsContent value="medical" className="space-y-3 pt-2">
            <Field label={`${t("medicalConditions")} (${t("separateComma")})`}><Textarea rows={2} value={csv(form.medical_conditions)} onChange={(e) => set("medical_conditions", toArr(e.target.value))} data-testid="resident-conditions" /></Field>
            <Field label={`${t("allergies")} (${t("separateComma")})`}><Input value={csv(form.allergies)} onChange={(e) => set("allergies", toArr(e.target.value))} /></Field>
            <Field label={`${t("dietaryRestrictions")} (${t("separateComma")})`}><Input value={csv(form.dietary_restrictions)} onChange={(e) => set("dietary_restrictions", toArr(e.target.value))} /></Field>
            <Field label={t("procedures")}><Textarea rows={2} value={form.procedures || ""} onChange={(e) => set("procedures", e.target.value)} /></Field>
            <Field label={t("careInstructions")}><Textarea rows={2} value={form.care_instructions || ""} onChange={(e) => set("care_instructions", e.target.value)} /></Field>
            <div className="flex items-center gap-3">
              <UploadButton label={t("uploadDocument")} accept="application/pdf,image/*,.doc,.docx" category="resident-doc" testId="upload-resident-doc"
                onUploaded={(d) => set("documents", [...(form.documents || []), { file_id: d.id, name: d.original_filename }])} />
              <div className="flex flex-wrap gap-1">{(form.documents || []).map((d, i) => <a key={i} href={fileDownloadUrl(d.file_id)} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded bg-muted flex items-center gap-1"><FileText className="w-3 h-3" />{d.name}</a>)}</div>
            </div>
          </TabsContent>
          <TabsContent value="contacts" className="space-y-3 pt-2">
            {(form.family_contacts || []).map((c, i) => {
              const upd = (k, v) => { const cs = [...form.family_contacts]; cs[i] = { ...cs[i], [k]: v }; set("family_contacts", cs); };
              return (
                <div key={i} className="rounded-lg border border-border p-3 space-y-3" data-testid="family-contact-row">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label={t("firstName")}><Input value={c.first_name ?? c.name ?? ""} onChange={(e) => upd("first_name", e.target.value)} data-testid={`family-firstname-${i}`} /></Field>
                    <Field label={t("lastName")}><Input value={c.last_name || ""} onChange={(e) => upd("last_name", e.target.value)} data-testid={`family-lastname-${i}`} /></Field>
                    <Field label={t("relationship")}><Input value={c.relationship || ""} onChange={(e) => upd("relationship", e.target.value)} /></Field>
                    <Field label={t("phone")}><Input value={c.phone || ""} onChange={(e) => upd("phone", e.target.value)} /></Field>
                    <Field label={t("email")} className="sm:col-span-2"><Input type="email" value={c.email || ""} onChange={(e) => upd("email", e.target.value)} data-testid={`family-email-${i}`} /></Field>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => set("family_contacts", form.family_contacts.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 mr-1" />{t("remove")}</Button>
                  </div>
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={() => set("family_contacts", [...(form.family_contacts || []), { first_name: "", last_name: "", relationship: "", phone: "", email: "" }])}><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
          </TabsContent>
          <TabsContent value="tasks" className="space-y-2 pt-2">
            <div className="flex justify-end"><Button size="sm" onClick={() => setTaskDlg(true)} data-testid="assign-rtask-button"><Plus className="w-4 h-4 mr-1" />{t("assignTask")}</Button></div>
            {tasks.map((tk) => (
              <div key={tk.id} className="flex items-center gap-2 p-2 rounded-lg border border-border text-sm">
                {tk.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
                <span className="flex-1">{tk.title}</span>
                <Badge variant="outline" className="text-[10px]">{t(tk.category)}</Badge>
                <span className="text-xs text-muted-foreground">{tk.date} {tk.time}</span>
              </div>
            ))}
            {tasks.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t("noData")}</p>}
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={save} data-testid="save-resident">{t("save")}</Button>
        </DialogFooter>
        <TaskDialog open={taskDlg} onOpenChange={setTaskDlg} resident={resident} employees={employees} onSaved={reloadTasks} />
      </DialogContent>
    </Dialog>
  );
}

export default function Residents() {
  const { t } = useT();
  const { isManager } = useCenter();
  const [residents, setResidents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [taskDlg, setTaskDlg] = useState({ open: false, resident: null });
  const [taskKey, setTaskKey] = useState(0);

  const load = () => api.get("/residents").then((r) => setResidents(r.data));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.active))); }, []);

  const del = async (id) => { if (!window.confirm(t("delete") + "?")) return; await api.delete(`/residents/${id}`); setSelectedId(null); load(); };
  const filtered = residents.filter((r) => r.full_name.toLowerCase().includes(q.toLowerCase()) || (r.room_number || "").includes(q) || (r.user_code || "").toLowerCase().includes(q.toLowerCase()));
  const selected = residents.find((r) => r.id === selectedId) || null;

  return (
    <div className="h-full flex overflow-hidden">
      {/* Master list */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-border flex flex-col shrink-0 ${selected ? "hidden md:flex" : "flex"}`}>
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h1 className="font-display text-2xl font-bold tracking-tight">{t("residents")}</h1>
            {isManager && <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="add-resident-button"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="resident-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-border">
          {filtered.map((r) => (
            <button key={r.id} onClick={() => setSelectedId(r.id)} data-testid="resident-row"
              className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors ${selectedId === r.id ? "bg-primary/10" : "hover:bg-accent/50"}`}>
              <UserAvatar name={r.full_name} src={r.photo} size={40} />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{r.full_name}</p>
                <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><BedDouble className="w-3 h-3" />{r.room_number || "—"}{r.user_code ? <span className="text-primary"> · {r.user_code}</span> : ""}{r.allergies?.length ? <span className="text-destructive"> · {r.allergies.join(", ")}</span> : ""}</p>
              </div>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-12 text-sm">{t("noData")}</p>}
        </div>
      </div>

      {/* Detail */}
      <div className={`flex-1 overflow-y-auto scrollbar-thin bg-background ${selected ? "block" : "hidden md:block"}`}>
        {selected ? (
          <ResidentDetail resident={selected} isManager={isManager} onSaved={load} onDelete={del}
            onBack={() => setSelectedId(null)} taskRefreshKey={taskKey}
            onAssignTask={(res) => setTaskDlg({ open: true, resident: res })} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center"><BedDouble className="w-6 h-6" /></div>
            <p className="text-sm">{t("residents")}</p>
          </div>
        )}
      </div>

      <ResidentDialog open={createOpen} onOpenChange={setCreateOpen} resident={null} employees={employees} onSaved={load} />
      <TaskDialog open={taskDlg.open} onOpenChange={(o) => setTaskDlg((d) => ({ ...d, open: o }))} resident={taskDlg.resident} employees={employees} onSaved={() => setTaskKey((k) => k + 1)} />
    </div>
  );
}
